/**
 * Resources Screen (Sales Rep)
 * 
 * Sales reps can view and access all published resources.
 * Beautiful browsing experience with categories and search.
 * 
 * @module ResourcesScreen
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Search,
  Filter,
  RefreshCw,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { ResourceService } from '../../services/ResourceService';
import { ResourceCard } from '../../components/shared/ResourceCard';
import { Resource, ResourceFilters, ResourceCategory, ResourceType } from '../../@types/resource';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const Subtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};

  @media (min-width: 768px) {
    gap: ${Spacing.md};
    justify-content: space-between;
    margin-bottom: ${Spacing.xl};
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: ${Spacing.md};
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  @media (min-width: 768px) {
    min-width: 300px;
  }

  svg {
    position: absolute;
    left: ${Spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 16px;
    height: 16px;

    @media (min-width: 768px) {
      left: ${Spacing.md};
      width: 18px;
      height: 18px;
    }
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.sm} ${Spacing.sm} 36px;
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 42px;
    border-radius: ${BorderRadius.lg};
    font-size: ${Typography.fontSize.md};
  }

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.main};
  background: white;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (min-width: 768px) {
    gap: ${Spacing.sm};
    padding: ${Spacing.sm} ${Spacing.md};
    font-size: ${Typography.fontSize.sm};
  }

  &:hover {
    background: ${ThemeColors.background.secondary};
    border-color: ${ThemeColors.primary.main};
  }

  svg {
    width: 14px;
    height: 14px;

    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.lg};

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${Spacing.md};
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: ${Spacing.md};
  }
`;

const CategoryCard = styled.div<{ active?: boolean }>`
  background: white;
  border: 2px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  @media (min-width: 768px) {
    padding: ${Spacing.md};
    border-radius: ${BorderRadius.lg};
  }

  &:hover {
    border-color: ${ThemeColors.primary.main};
    box-shadow: ${Shadows.sm};

    @media (min-width: 768px) {
      box-shadow: ${Shadows.md};
    }
  }

  ${props => props.active && `
    background: ${ThemeColors.primary.light};
    box-shadow: ${Shadows.sm};

    @media (min-width: 768px) {
      box-shadow: ${Shadows.md};
    }
  `}
`;

const CategoryIcon = styled.div`
  font-size: 20px;
  margin-bottom: ${Spacing.xs};

  @media (min-width: 768px) {
    font-size: 24px;
    margin-bottom: ${Spacing.sm};
  }
`;

const CategoryName = styled.div`
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};

  @media (min-width: 768px) {
    font-size: ${Typography.fontSize.sm};
  }
`;

const CategoryCount = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};

  @media (min-width: 768px) {
    font-size: ${Typography.fontSize.sm};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${Spacing.lg};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${Spacing.md};
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const EmptyDescription = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl};
`;

const FeaturedSection = styled.div`
  background: linear-gradient(135deg, ${ThemeColors.primary.light}, ${ThemeColors.secondary.light});
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.xl};
  margin-bottom: ${Spacing.lg};
`;

const FeaturedTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${Spacing.lg};
`;

const RegularSection = styled.div``;

/**
 * ResourcesScreen Component
 */
export const ResourcesScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [resources, setResources] = useState<Resource[]>([]);
  const [featuredResources, setFeaturedResources] = useState<Resource[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory | 'all'>('all');

  /**
   * Fetch resources
   */
  const fetchResources = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filters: ResourceFilters = {
        status: 'published',
        visibility: 'all' // Will also fetch sales-reps-only in service logic
      };

      if (selectedCategory !== 'all') {
        filters.category = selectedCategory;
      }

      if (searchTerm) {
        filters.searchTerm = searchTerm;
      }

      console.log('Fetching resources with filters:', filters);
      const { resources: fetchedResources } = await ResourceService.getResources(
        filters,
        { field: 'createdAt', direction: 'desc' }
      );

      console.log('Fetched resources:', fetchedResources.length, fetchedResources);

      // Separate featured resources
      const featured = fetchedResources.filter(r => r.featured);
      const regular = fetchedResources.filter(r => !r.featured);

      console.log('Featured resources:', featured.length);
      console.log('Regular resources:', regular.length);

      setFeaturedResources(featured);
      // Show all resources in the main list (including featured for now)
      // If you want to exclude featured from main list, use: setResources(regular);
      setResources(fetchedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      // Set empty arrays on error to show empty state
      setResources([]);
      setFeaturedResources([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  /**
   * Fetch statistics
   */
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await ResourceService.getResourceStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  /**
   * Load data
   */
  useEffect(() => {
    fetchResources();
    fetchStatistics();
  }, [fetchResources, fetchStatistics]);

  /**
   * Handle resource click
   */
  const handleResourceClick = useCallback((resource: Resource) => {
    navigate(`/resources/${resource.id}`);
  }, [navigate]);

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>{t('resources.title', 'Resource Library')}</Title>
          <Subtitle>{t('resources.subtitle', 'Access training materials, guides, and sales resources')}</Subtitle>
        </Header>

        {/* Search & Filters */}
        <Toolbar>
          <SearchBar>
            <Search />
            <SearchInput
              type="text"
              placeholder={t('resources.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <Button onClick={fetchResources}>
            <RefreshCw />
            {t('common.refresh')}
          </Button>
        </Toolbar>

        {/* Categories */}
        <CategoryGrid>
          <CategoryCard
            active={selectedCategory === 'all'}
            onClick={() => setSelectedCategory('all')}
          >
            <CategoryIcon>📚</CategoryIcon>
            <CategoryName>{t('resources.allResources')}</CategoryName>
            <CategoryCount>{statistics?.totalResources || 0}</CategoryCount>
          </CategoryCard>
          {ResourceService.CATEGORIES.slice(0, 5).map(category => (
            <CategoryCard
              key={category.id}
              active={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryName>{t(`resources.categories.${category.id}`)}</CategoryName>
              <CategoryCount>
                {statistics?.resourcesByCategory[category.id] || 0}
              </CategoryCount>
            </CategoryCard>
          ))}
        </CategoryGrid>

        <Content>
          {/* Featured Resources */}
          {featuredResources.length > 0 && (
            <FeaturedSection>
              <FeaturedTitle>
                <Sparkles size={24} color={ThemeColors.primary.main} />
                {t('resources.featuredResources')}
              </FeaturedTitle>
              <FeaturedGrid>
                {featuredResources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onClick={() => handleResourceClick(resource)}
                  />
                ))}
              </FeaturedGrid>
            </FeaturedSection>
          )}

          {/* Regular Resources */}
          <RegularSection>
            <SectionTitle>
              <TrendingUp size={20} color={ThemeColors.text.secondary} />
              {t('resources.allResources')} ({resources.length})
            </SectionTitle>
            
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: Spacing.xxl, color: ThemeColors.text.secondary }}>
                {t('resources.loading')}
              </div>
            ) : resources.length > 0 ? (
              <ResourceGrid>
                {resources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onClick={() => handleResourceClick(resource)}
                  />
                ))}
              </ResourceGrid>
            ) : (
              <EmptyState>
                <EmptyIcon>📄</EmptyIcon>
                <EmptyTitle>{t('resources.noResourcesFound')}</EmptyTitle>
                <EmptyDescription>
                  {searchTerm || selectedCategory !== 'all' 
                    ? t('resources.tryAdjustingFilters')
                    : t('resources.noResourcesAvailable')
                  }
                </EmptyDescription>
              </EmptyState>
            )}
          </RegularSection>
        </Content>
      </MainContent>
    </PageContainer>
  );
};

